import { useEffect, useState, ChangeEvent } from "react";
import { Button } from "@mui/material";

import "./style.scss";
import {
    patchGreeting,
    patchDescription,
    patchSampleQuestions
} from "../../../api/admin/config/endpoints-referrers";
import { defaultAssistantSettings as defaultConfig } from "../../../common/utilities/constants/constants";
import { trim } from "../../../common/utilities/text/text-formatter.util";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { LoaderView } from "../../../components/loader/loader.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";

const AssistantSettingsView = () => {
    const { config, refreshConfig } = FirebaseAuthContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [header, setHeader] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [prompts, setPrompts] = useState<string[]>([]);

    useEffect(() => {
        if (!config) return;

        setHeader(config.greeting);
        setDescription(config.description);
        setPrompts(config.suggestedQuestions.map(item => item.question));
        setPrompts(
            [...Array(6)].map((_, i) =>
                config.suggestedQuestions.length > i
                    ? config.suggestedQuestions[i].question
                    : defaultConfig.sampleQuestions.length > i
                      ? defaultConfig.sampleQuestions[i]
                      : ""
            )
        );
        setIsLoading(false);
    }, [config]);

    const hasEmptySample = () => {
        if (prompts.length < 6) return true;

        return prompts.find(item => !trim(item)) !== undefined;
    };

    const isDefaultValues = () => {
        if (!config || config.suggestedQuestions.length < 6 || prompts.length < 6) return false;

        return !(
            config.greeting !== header ||
            config.description !== description ||
            prompts.find((item, i) => config.suggestedQuestions[i].question !== item)
        );
    };

    const handleSaveClick = () => {
        updateConfig();
    };

    const updateConfig = async () => {
        try {
            setIsLoading(true);
            await patchGreeting(trim(header));
            await patchDescription(trim(description));
            await patchSampleQuestions(prompts.map(item => trim(item)));
            await refreshConfig();
        } catch (error) {
            console.info(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleHeaderChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
        const val = evt.target?.value;
        setHeader(val ?? "");
    };

    const handleDescriptionChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
        const val = evt.target?.value;
        setDescription(val ?? "");
    };

    const handlePromptChange = (evt: ChangeEvent<HTMLTextAreaElement>, index: number) => {
        const val = evt.target?.value;
        setPrompts(prevPrompts => prevPrompts.map((item, i) => (i === index ? (val ?? "") : item)));
    };

    return (
        <HeaderLayout
            subheaderHeight={46}
            subheader={
                <div className="assistant-settings-header">
                    <div className="assistant-settings-header-title">
                        <span className="assistant-settings-header-title__text">
                            Assistant StartUp Screen Settings
                        </span>
                    </div>
                </div>
            }
            footerHeight={60}
            footer={
                <div style={{ height: 60 }} className="assistant-settings-footer">
                    <div className="assistant-settings-footer-left">
                        <Button
                            className="primary-button"
                            variant="outlined"
                            disabled={
                                !trim(header) ||
                                !trim(description) ||
                                hasEmptySample() ||
                                isDefaultValues()
                            }
                            onClick={handleSaveClick}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="assistant-settings-view">
                <div className="assistant-settings-body">
                    <div className="assistant-settings-content">
                        <div className="assistant-settings-item">
                            <span className="assistant-settings-item__text">Header:</span>
                            <textarea
                                className="assistant-settings-item__input assistant-settings-item__input--header"
                                onChange={handleHeaderChange}
                                value={header}
                                maxLength={60}
                            />
                        </div>
                        <div className="assistant-settings-item">
                            <span className="assistant-settings-item__text">Description:</span>
                            <textarea
                                className="assistant-settings-item__input assistant-settings-item__input--description"
                                onChange={handleDescriptionChange}
                                value={description}
                                maxLength={200}
                            />
                        </div>
                        {prompts.map((item, index) => (
                            <div key={index} className="assistant-settings-item">
                                <span className="assistant-settings-item__text">
                                    Prompt {index + 1}:
                                </span>
                                <textarea
                                    className="assistant-settings-item__input assistant-settings-item__input--prompt"
                                    onChange={event => handlePromptChange(event, index)}
                                    value={item}
                                    maxLength={
                                        index === 0 || index === 1 ? 90 : index === 2 ? 150 : 60
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <LoaderView show={isLoading} />
            </div>
        </HeaderLayout>
    );
};

export default AssistantSettingsView;
